import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useRevalidator } from 'react-router';
import type { TFunction } from 'i18next';
import { Switch } from '@artosera/ui';
import { CATEGORIES_MODULE } from '@artosera/shared';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
import { modeDemonstrationActif } from '../../api/demo.js';
import { raisonErreur } from '../../api/demandeSite.js';
import {
  useBasculerModule,
  useModulesGalerie,
  type ModuleGalerie,
  type ModulesGalerie,
} from '../../api/modulesGalerie.js';
import { estApiError } from '../../api/errors.js';
import styles from './ModulesPage.module.css';

/** Les groupes de la page : les catégories du registre, puis les services. */
const GROUPE_SERVICES = 'services';
const GROUPE_AUTRES = 'autres';

function groupeDe(module: ModuleGalerie): string {
  if (module.famille === 'service') return GROUPE_SERVICES;
  return module.categorie ?? GROUPE_AUTRES;
}

function libelles(t: TFunction, ids: readonly string[]): string {
  return ids.map((id) => t(`commun:modules.${id}.libelle`)).join(', ');
}

export function ModulesPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const autorise = galerieALaction(galerie, 'settings:modules');
  const requete = useModulesGalerie(galerieSlug, autorise);
  if (!autorise) return <p role="alert">{t('reglages.modules.interdit')}</p>;
  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('reglages.modules.titre')}</h1>
      <p className={styles.intro}>{t('reglages.modules.intro')}</p>
      <p className={styles.intro}>{t('reglages.modules.rassurance')}</p>
      {requete.isPending ? <p role="status">{t('reglages.modules.chargement')}</p> : null}
      {requete.isError ? <p role="alert">{t('reglages.modules.erreur')}</p> : null}
      {requete.data ? <Liste donnees={requete.data} galerieSlug={galerieSlug} /> : null}
    </div>
  );
}

function Liste({
  donnees,
  galerieSlug,
}: {
  donnees: ModulesGalerie;
  galerieSlug: string;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const mutation = useBasculerModule(galerieSlug);
  const revalidateur = useRevalidator();
  const demo = modeDemonstrationActif();
  const actifs = new Set(donnees.modules.filter((m) => m.actif).map((m) => m.id));

  const groupes = [...CATEGORIES_MODULE, GROUPE_SERVICES, GROUPE_AUTRES]
    .map((cle) => ({ cle, modules: donnees.modules.filter((m) => groupeDe(m) === cle) }))
    .filter((g) => g.modules.length > 0);

  return (
    <>
      {!donnees.interrupteursLisibles ? (
        <p className={styles.avis} role="note">
          {t('reglages.modules.illisibles')}
        </p>
      ) : null}
      {demo ? <p className={styles.avis}>{t('reglages.modules.demo')}</p> : null}
      {groupes.map((groupe) => (
        <section
          key={groupe.cle}
          className={styles.groupe}
          aria-labelledby={`groupe-${groupe.cle}`}
        >
          <h2 id={`groupe-${groupe.cle}`} className={styles.groupeTitre}>
            {t(`reglages.modules.categories.${groupe.cle}`)}
          </h2>
          <ul className={styles.liste}>
            {groupe.modules.map((module) => (
              <LigneModule
                key={module.id}
                module={module}
                dependantsActifs={module.dependants.filter((id) => actifs.has(id))}
                desactive={demo || mutation.isPending}
                enCours={mutation.isPending && mutation.variables.module === module.id}
                erreur={
                  mutation.isError && mutation.variables.module === module.id
                    ? mutation.error
                    : null
                }
                onBasculer={(actif) => {
                  mutation.mutate(
                    { module: module.id, actif, revision: donnees.revision },
                    {
                      onSuccess: () => {
                        void revalidateur.revalidate();
                      },
                    },
                  );
                }}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

function LigneModule({
  module,
  dependantsActifs,
  desactive,
  enCours,
  erreur,
  onBasculer,
}: {
  module: ModuleGalerie;
  dependantsActifs: readonly string[];
  desactive: boolean;
  enCours: boolean;
  erreur: unknown;
  onBasculer: (actif: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const libelle = t(`commun:modules.${module.id}.libelle`);
  const reserve = module.disponibilite === 'reserve';
  // Un module déjà actif garde son interrupteur pour être éteint, même fermé.
  const interrupteur = !reserve && (module.disponibilite === 'activable' || module.actif);

  let statut: string | null = null;
  if (reserve) {
    statut = t('reglages.modules.reserve');
  } else if (module.disponibilite !== 'activable') {
    statut = t(`reglages.modules.disponibilite.${module.disponibilite}`);
  }

  let signalement: string | null = null;
  if (module.actif && !module.effectif) {
    signalement =
      module.dependancesManquantes.length > 0
        ? t('reglages.modules.dependanceManquante', {
            modules: libelles(t, module.dependancesManquantes),
          })
        : t('reglages.modules.ferme');
  }

  return (
    <li className={styles.ligne}>
      <div className={styles.textes}>
        <h3 className={styles.nom}>{libelle}</h3>
        <p className={styles.description}>{t(`commun:modules.${module.id}.description`)}</p>
        {module.dependances.length > 0 ? (
          <p className={styles.note}>
            {t('reglages.modules.necessite', { modules: libelles(t, module.dependances) })}
          </p>
        ) : null}
        {statut ? <p className={styles.statut}>{statut}</p> : null}
        {signalement ? <p className={styles.signalement}>{signalement}</p> : null}
        {module.actif && dependantsActifs.length > 0 ? (
          <p className={styles.note} id={`extinction-${module.id}`}>
            {t('reglages.modules.extinction', {
              count: dependantsActifs.length,
              modules: libelles(t, dependantsActifs),
            })}
          </p>
        ) : null}
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {messageErreur(t, erreur)}
          </p>
        ) : null}
      </div>
      {interrupteur ? (
        <div className={styles.commande}>
          <Switch
            id={`module-${module.id}`}
            label={libelle}
            labelVisible={false}
            aria-describedby={
              module.actif && dependantsActifs.length > 0 ? `extinction-${module.id}` : undefined
            }
            checked={module.actif}
            disabled={desactive}
            pending={enCours}
            onCheckedChange={onBasculer}
          />
        </div>
      ) : null}
    </li>
  );
}

function messageErreur(t: TFunction, erreur: unknown): string {
  const raison = raisonErreur(erreur);
  if (raison === 'dependances_manquantes') {
    const details = estApiError(erreur) ? (erreur.details as { dependances?: unknown }) : null;
    const ids = Array.isArray(details?.dependances)
      ? details.dependances.filter((id): id is string => typeof id === 'string')
      : [];
    return t('reglages.modules.erreurs.dependances_manquantes', { modules: libelles(t, ids) });
  }
  if (
    raison === 'module_indisponible' ||
    raison === 'revision_modules_perimee' ||
    raison === 'module_reserve_console'
  ) {
    return t(`reglages.modules.erreurs.${raison}`);
  }
  return t('reglages.modules.erreurs.generique');
}
